/** Форматирование веб-карточки турнира: «сб 7 ноя · 11:00», «пт 6 ноя 20:00», «12 000 ₽», инициалы команд. */

export type ZonedParts = Readonly<{
  weekday: number;
  day: number;
  month: number;
  hour: number;
  minute: number;
}>;

/** Турниры проходят в Москве: сроки записи и жеребьёвки показываем по московскому времени. */
export const TOURNAMENT_TIME_ZONE = 'Europe/Moscow';

const RU_MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const RU_WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const EN_WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/u;
const TIME_ONLY = /^(\d{2}):(\d{2})/u;

const pad = (value: number) => String(value).padStart(2, '0');

const intlLocale = (locale: string) => (locale === 'ru' ? 'ru-RU' : locale === 'kk' ? 'kk-KZ' : 'en-GB');

export const getZonedParts = (date: Date, timeZone: string): ZonedParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';

  return {
    weekday: EN_WEEKDAYS[value('weekday')] ?? 0,
    day: Number(value('day')),
    month: Number(value('month')),
    hour: Number(value('hour')) % 24,
    minute: Number(value('minute')),
  };
};

/** «сб 7 ноя» (ru) / «Sat 7 Nov» (en) / через Intl для kk. */
export const formatDayLabel = (date: Date, locale: string, timeZone: string): string => {
  const parts = getZonedParts(date, timeZone);
  if (locale === 'ru') return `${RU_WEEKDAYS[parts.weekday]} ${parts.day} ${RU_MONTHS[parts.month - 1]}`;

  const weekday = new Intl.DateTimeFormat(intlLocale(locale), { timeZone, weekday: 'short' }).format(date);
  const month = new Intl.DateTimeFormat(intlLocale(locale), { timeZone, month: 'short' }).format(date);

  return `${weekday} ${parts.day} ${month}`;
};

export const formatClock = (date: Date, timeZone: string): string => {
  const parts = getZonedParts(date, timeZone);

  return `${pad(parts.hour)}:${pad(parts.minute)}`;
};

/** Дата (`YYYY-MM-DD…`) и время (`HH:mm…`) турнира как «стенные» часы зала, без сдвига часовых поясов. */
export const parseTournamentStart = (startDate?: string | null, startTime?: string | null): Date | null => {
  const dateMatch = startDate?.match(DATE_ONLY);
  if (!dateMatch) return null;
  const timeMatch = startTime?.match(TIME_ONLY);
  const date = new Date(
    Date.UTC(
      Number(dateMatch[1]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[3]),
      timeMatch ? Number(timeMatch[1]) : 0,
      timeMatch ? Number(timeMatch[2]) : 0,
    ),
  );

  const valid =
    !Number.isNaN(date.getTime()) &&
    date.getUTCMonth() === Number(dateMatch[2]) - 1 &&
    date.getUTCDate() === Number(dateMatch[3]) &&
    (!timeMatch || (Number(timeMatch[1]) < 24 && Number(timeMatch[2]) < 60));

  return valid ? date : null;
};

/** «сб 7 ноя · 11:00»; без времени — «сб 7 ноя». */
export const formatTournamentDateLine = (
  startDate?: string | null,
  startTime?: string | null,
  locale = 'ru',
): string | null => {
  const start = parseTournamentStart(startDate, startTime);
  if (!start) return null;
  const day = formatDayLabel(start, locale, 'UTC');

  return TIME_ONLY.test(startTime ?? '') ? `${day} · ${formatClock(start, 'UTC')}` : day;
};

/** «пт 6 ноя 20:00» по московскому времени — срок записи из ISO-таймстампа. */
export const formatDeadline = (iso?: string | null, locale = 'ru', timeZone = TOURNAMENT_TIME_ZONE): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return `${formatDayLabel(date, locale, timeZone)} ${formatClock(date, timeZone)}`;
};

/** «18 окт» — короткая дата без дня недели (прошлый тур). */
export const formatShortDate = (iso?: string | null, locale = 'ru', timeZone = 'UTC'): string | null => {
  const start = parseTournamentStart(iso);
  if (!start) return null;
  const parts = getZonedParts(start, timeZone);
  if (locale === 'ru') return `${parts.day} ${RU_MONTHS[parts.month - 1]}`;

  return `${parts.day} ${new Intl.DateTimeFormat(intlLocale(locale), { timeZone, month: 'short' }).format(start)}`;
};

/** «12 000 ₽» из минорных единиц; пробелы нормализованы, неизвестная валюта не роняет страницу. */
export const formatMoneyMinor = (amountMinor: number, currency = 'RUB', locale = 'ru'): string => {
  const amount = Math.round(amountMinor / 100);
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    })
      .format(amount)
      .replace(/\s/gu, ' ');
  } catch {
    return `${amount} ${currency}`;
  }
};

/** «Атланты» → «Ат», «Блок-Пост» → «Бп»: первая буква и первая буква второго слова либо вторая буква. */
export const getTeamInitials = (name: string): string => {
  const words = name
    .trim()
    .split(/[\s-]+/u)
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length >= 2) return `${words[0].charAt(0).toUpperCase()}${words[1].charAt(0).toLowerCase()}`;

  return `${words[0].charAt(0).toUpperCase()}${words[0].charAt(1).toLowerCase()}`;
};

/** Русские формы для серверных текстов (OG-картинка): pluralRu(4, ['команда', 'команды', 'команд']). */
export const pluralRu = (count: number, forms: readonly [string, string, string]): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];

  return forms[2];
};
